import { pageNumbers } from "@vidario/domain";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactElement, type ReactNode } from "react";

import { cn } from "../utils/cn";

export interface PaginationProps {
  /** 1-based. */
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** E.g. "1–24 von 441 Kandidaten"; shown even when everything fits on one page. */
  summary?: ReactNode;
  className?: string;
}

const ITEM =
  "inline-flex h-9 min-w-9 items-center justify-center rounded-md px-2 text-sm font-medium " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500";
const IDLE = "text-text-secondary hover:bg-neutral-100 hover:text-text-primary";
const CURRENT = "bg-primary-600 text-neutral-0";
const DISABLED = "disabled:pointer-events-none disabled:opacity-40";

/** Numbered paging below a list (#14): ‹ 1 … 9 10 11 … 19 ›. */
export function Pagination({
  page,
  pageCount,
  onPageChange,
  summary,
  className,
}: PaginationProps): ReactElement {
  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-3", className)}>
      {summary ? <p className="m-0 text-sm text-text-secondary">{summary}</p> : <span />}
      {pageCount > 1 && (
        <nav aria-label="Seiten">
          <ul className="m-0 flex list-none items-center gap-1 p-0">
            <li>
              <button
                type="button"
                aria-label="Vorherige Seite"
                className={cn(ITEM, IDLE, DISABLED)}
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
              >
                <ChevronLeft size={16} aria-hidden="true" />
              </button>
            </li>
            {pageNumbers(page, pageCount).map((item, i) =>
              item === "gap" ? (
                <li key={`gap-${i}`} aria-hidden="true" className="px-1 text-sm text-text-tertiary">
                  …
                </li>
              ) : (
                <li key={item}>
                  <button
                    type="button"
                    aria-label={`Seite ${item}`}
                    aria-current={item === page ? "page" : undefined}
                    className={cn(ITEM, item === page ? CURRENT : IDLE)}
                    onClick={() => onPageChange(item)}
                  >
                    {item}
                  </button>
                </li>
              ),
            )}
            <li>
              <button
                type="button"
                aria-label="Nächste Seite"
                className={cn(ITEM, IDLE, DISABLED)}
                disabled={page >= pageCount}
                onClick={() => onPageChange(page + 1)}
              >
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}
